import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiService } from '../services/api';
import {
  DIRECT_CUSTOMERS_SELECTION,
  isSelectionStale,
  resolveInitialTenantSelection,
  TENANT_SELECTION_STORAGE_KEY,
  type TenantSelection,
} from './tenantSelection';

/** Tenant-Filter für Super-Admins — es ist immer genau ein Tenant aktiv, nie "Alle Tenants". */
interface TenantFilterContextEnabledValue {
  enabled: true;
  selectedTenant: TenantSelection;
  setSelectedTenant: (selection: TenantSelection) => void;
}

/** Tenant-Filter für Nicht-Super-Admins — keine Auswahl, kein Filter. */
interface TenantFilterContextDisabledValue {
  enabled: false;
}

type TenantFilterContextValue = TenantFilterContextEnabledValue | TenantFilterContextDisabledValue;

const DISABLED_CONTEXT_VALUE: TenantFilterContextDisabledValue = { enabled: false };

const TenantFilterContext = createContext<TenantFilterContextValue>(DISABLED_CONTEXT_VALUE);

interface TenantFilterProviderProps {
  /** Nur Super-Admins dürfen Tenants listen (Backend: SUPER_ADMIN). */
  enabled: boolean;
  children: ReactNode;
}

export function TenantFilterProvider({ enabled, children }: TenantFilterProviderProps) {
  const [selectedTenant, setSelectedTenant] = useState<TenantSelection>(() =>
    resolveInitialTenantSelection(enabled ? localStorage.getItem(TENANT_SELECTION_STORAGE_KEY) : null),
  );
  const staleCheckDone = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    localStorage.setItem(TENANT_SELECTION_STORAGE_KEY, JSON.stringify(selectedTenant));
  }, [enabled, selectedTenant]);

  // Einmaliger Abgleich: zeigt die gespeicherte Auswahl noch auf einen existierenden
  // Tenant? Ein `useRef`-Guard verhindert wiederholte Aufrufe, ohne `selectedTenant`
  // aus den Deps zu nehmen (kein eslint-disable nötig).
  useEffect(() => {
    if (!enabled || staleCheckDone.current) return;
    if (selectedTenant.id === DIRECT_CUSTOMERS_SELECTION.id) {
      staleCheckDone.current = true;
      return;
    }
    staleCheckDone.current = true;
    apiService
      .getAdminTenants({ search: selectedTenant.id, limit: 1 })
      .then((result) => {
        if (isSelectionStale(selectedTenant, result.data)) {
          setSelectedTenant(DIRECT_CUSTOMERS_SELECTION);
        }
      })
      .catch(() => undefined);
  }, [enabled, selectedTenant]);

  const value: TenantFilterContextValue = enabled
    ? { enabled: true, selectedTenant, setSelectedTenant }
    : DISABLED_CONTEXT_VALUE;

  return <TenantFilterContext.Provider value={value}>{children}</TenantFilterContext.Provider>;
}

/**
 * Liefert die globale Tenant-Auswahl. Seiten filtern damit ihre Listen
 * backendseitig (z.B. `tenantId` an `GET /api/admin/customers`).
 * @returns Discriminated Union — bei `enabled: false` existiert keine Auswahl.
 */
export function useTenantFilter(): TenantFilterContextValue {
  return useContext(TenantFilterContext);
}
